import { getQueryClientInstance } from '@/api/queries/queryClient';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const COUNTRY_OVERRIDE_STORAGE_KEY = 'ces-country-override';

export interface CountryOverrideSimulation {
  countries: string[];
  simulateCommercialGroup: boolean;
}

interface CountryOverrideState extends CountryOverrideSimulation {
  applySimulation: (simulation: CountryOverrideSimulation) => void;
  clear: () => void;
}

// Trim, drop blanks, upper-case, and dedupe while preserving selection order so the header value is
// stable and the backend receives canonical CESAPP_XX tokens.
const normalizeCountries = (countries: string[]): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const raw of countries) {
    const code = raw.trim().toUpperCase();
    if (code && !seen.has(code)) {
      seen.add(code);
      normalized.push(code);
    }
  }
  return normalized;
};

// Drop the whole cache (not just invalidate) so no all-access results linger on screen after the
// simulated scope changes; queries then refetch with the new (or absent) x-locale-override header.
const resetCubeCache = (): void => {
  getQueryClientInstance().clear();
};

const useCountryOverrideStore = create<CountryOverrideState>()(
  persist(
    (set): CountryOverrideState => ({
      countries: [],
      simulateCommercialGroup: false,
      applySimulation: ({ countries, simulateCommercialGroup }: CountryOverrideSimulation) => {
        const normalized = normalizeCountries(countries);
        // getUserInfo only reads the group flag while a country is simulated, so never store a
        // combination the auth layer would silently ignore.
        set({ countries: normalized, simulateCommercialGroup: normalized.length > 0 && simulateCommercialGroup });
        resetCubeCache();
      },
      clear: () => {
        set({ countries: [], simulateCommercialGroup: false });
        resetCubeCache();
      },
    }),
    {
      name: COUNTRY_OVERRIDE_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state: CountryOverrideState) => ({
        countries: state.countries,
        simulateCommercialGroup: state.simulateCommercialGroup,
      }),
    },
  ),
);

export default useCountryOverrideStore;
